import { useState, useCallback, useRef, useEffect } from "react";
import axios from "../services/axios";
import { InstallationTestStatus } from "../types/installationTest";

const MAX_TEST_DURATION = 5 * 60 * 1000;
const POLLING_INTERVAL = 5000; 

interface UseInstallationTestOptions {
  deviceId: string | undefined;
  enabled?: boolean;
}

export const useInstallationTest = ({ deviceId, enabled = true }: UseInstallationTestOptions) => {
  const [status, setStatus] = useState<InstallationTestStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const resetInstallTestInShadow = useCallback(async (silent: boolean = false) => {
    if (!deviceId) return;
    try {
      const response = await axios.post(`/api-v1/device/${deviceId}/stop-installation-test/`);
      return response.data.status === "test_stopped";
    } catch (err) {
      if (silent) {
        console.error("Failed to reset install_test in shadow:", err);
        return false;
      }
      throw err;
    }
  }, [deviceId]);

  const fetchStatus = useCallback(async () => {
    if (!deviceId) return;

    try {
      const response = await axios.get(`/api-v1/device/${deviceId}/installation-test-status/`);
      const data: InstallationTestStatus = response.data;
      setStatus(data);

      if (data.has_results) {
        stopPolling();
        setLoading(false);
        startTimeRef.current = null;
      }

      if (startTimeRef.current && Date.now() - startTimeRef.current > MAX_TEST_DURATION) {
        stopPolling();
        setError("Test timeout: The test is taking longer than expected. Please try again.");
        setLoading(false);
        startTimeRef.current = null;
        await resetInstallTestInShadow(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || "Failed to fetch test status");
      setLoading(false);
      stopPolling();
    }
  }, [deviceId, stopPolling, resetInstallTestInShadow]);

  const startTest = useCallback(async () => {
    if (!deviceId) return;

    setLoading(true);
    setError(null);
    startTimeRef.current = Date.now();

    try {
      const response = await axios.post(`/api-v1/device/${deviceId}/start-installation-test/`);
      if (response.data.status === "test_started") {
        await fetchStatus();
        setLoading(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || "Failed to start installation test");
      setLoading(false);
    }
  }, [deviceId, fetchStatus]);

  const stopTest = useCallback(async () => {
    if (!deviceId) return;

    setLoading(true);
    setError(null);

    try {
      const stopped = await resetInstallTestInShadow();
      if (stopped) {
        await fetchStatus();
        setLoading(false);
        startTimeRef.current = null;
      } else {
        setError("Failed to stop installation test");
        setLoading(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || "Failed to stop installation test");
      setLoading(false);
    }
  }, [deviceId, fetchStatus, resetInstallTestInShadow]);

  useEffect(() => {
    if (enabled && deviceId) {
      fetchStatus();
    }
  }, [enabled, deviceId, fetchStatus]);

  useEffect(() => {
    stopPolling();

    if (enabled && status?.install_test && !status?.has_results) {
      const interval = setInterval(() => {
        fetchStatus();
      }, POLLING_INTERVAL);

      pollingIntervalRef.current = interval;

      return () => {
        clearInterval(interval);
      };
    }
  }, [enabled, status?.install_test, status?.has_results, fetchStatus, stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const getElapsedTime = () => {
    if (!startTimeRef.current) return null;
    return Math.floor((Date.now() - startTimeRef.current) / 1000);
  };

  return {
    status,
    loading,
    error,
    startTest,
    stopTest,
    fetchStatus,
    clearError: () => setError(null),
    getElapsedTime,
  };
};

